import { defineStore } from "pinia"
import type { CustomPostTypeField } from "~/models/custom-post-type"
import type { Post, PostFeature, PostImageType, PostInfo, PostStatus, PostType, ProductKind } from "~/models/post"
import type { ApiResponse } from "~/types/api"
import type { IconType } from "~/types/base-icons"

export type PostKind = {
	title: string
	value: string
	icon: IconType
}

export type PostStatusType = {
	name: string
	name2?: string
	name3?: string
	value: PostStatus | PostType | any
	icon?: string
	maxLevel?: number
	hasTitle?: boolean
	hasContent?: boolean
	hasLocation?: boolean
	hasInfo?: boolean
	hasFeatures?: boolean
	hasOptions?: boolean
	hasCategory?: boolean
	hasKeyword?: boolean
	hasImages?: boolean
	hasCovers?: boolean
	hasVideos?: boolean
	hasProductInfo?: boolean
	hasSteps?: boolean
	hasCustomData?: boolean
	customFields?: { key: string; label: string }[]
	borderClass?: string
	bgClass?: string
	txtClass?: string
}

export const usePostStore = defineStore("post", () => {
	const { $i18n } = useNuxtApp()

	const state = {
		articles: ref<Post[]>([]),
		types: ref<PostStatusType[]>([
			{
				name: $i18n.t("article_categories"),
				name2: $i18n.t("articles"),
				name3: $i18n.t("article"),
				value: "article",
				icon: "mdi:newspaper-variant",
				maxLevel: 1,
				hasTitle: true,
				hasContent: true,
				hasLocation: false,
				hasInfo: false,
				hasFeatures: false,
				hasCategory: true,
				hasKeyword: true,
				hasImages: false,
				hasCovers: true,
				hasVideos: true,
				hasProductInfo: false
			},
			{
				name: $i18n.t("help_categories"),
				name2: $i18n.t("helps"),
				name3: $i18n.t("help"),
				value: "help",
				icon: "mdi:help-circle",
				maxLevel: 1,
				hasTitle: true,
				hasContent: true,
				hasLocation: false,
				hasInfo: false,
				hasFeatures: false,
				hasCategory: true,
				hasKeyword: true,
				hasImages: false,
				hasCovers: true,
				hasVideos: true,
				hasSteps: true,
				hasProductInfo: false
			},
			{
				name: $i18n.t("product_options"),
				name2: $i18n.t("product_options"),
				name3: $i18n.t("product_option"),
				value: "product-options",
				icon: "mdi:tune-variant",
				maxLevel: 2,
				hasTitle: false,
				hasContent: false,
				hasLocation: false,
				hasInfo: false,
				hasFeatures: false,
				hasCategory: true,
				hasImages: false,
				hasCovers: false,
				hasProductInfo: false
			},
			{
				name: $i18n.t("product_categories"),
				name2: $i18n.t("products"),
				name3: $i18n.t("product"),
				value: "product",
				icon: "mdi:package-variant",
				maxLevel: 1,
				hasTitle: true,
				hasContent: true,
				hasLocation: false,
				hasInfo: true,
				hasFeatures: true,
				hasCategory: true,
				hasKeyword: true,
				hasImages: true,
				hasCovers: true,
				hasVideos: true,
				hasProductInfo: true
			}
		]),
		statuses: ref<PostStatusType[]>([
			{
				name: $i18n.t("draft"),
				value: "draft",
				txtClass: "text-gray-500"
			},
			{
				name: $i18n.t("published"),
				value: "publish",
				txtClass: "text-blue-500"
			},
			{
				name: $i18n.t("trash"),
				value: "trash",
				txtClass: "text-red-500"
			}
		]),
		kinds: ref<PostKind[]>([])
	}

	const getTempPost = async (params?: GetTempPostRequest) => {
		const data = await $fetch<GetTempPostResponse>(GetTempPost.url(), {
			...useFetchConfig(),
			method: GetTempPost.method,
			body: params
		})
		return data.data
	}

	const getPost = async (formData: GetPostRequest) => {
		const data = await $fetch<GetPostResponse>(GetPost.url(formData.id), {
			...useFetchConfig(),
			method: GetPost.method,
			params: formData
		})
		return data.data
	}

	const getPostBySlug = async (slug: string) => {
		const data = await $fetch<GetPostBySlugResponse>(GetPostBySlug.url(slug), {
			...useFetchConfig(),
			method: GetPostBySlug.method
		})
		return data.data
	}

	const searchPosts = async (formData: SearchPostsRequest) => {
		const data = await $fetch<SearchPostsResponse>(SearchPosts.url(), {
			...useFetchConfig(),
			method: SearchPosts.method,
			query: formData
		})
		return data.data
	}

	const createPost = async (formData: CreatePostRequest) => {
		const data = await $fetch<CreatePostResponse>(CreatePost.url(), {
			...useFetchConfig(),
			method: CreatePost.method,
			body: formData
		})
		return data.data
	}

	const editPost = async (formData: EditPostRequest) => {
		const data = await $fetch<EditPostResponse>(EditPost.url(), {
			...useFetchConfig(),
			method: EditPost.method,
			body: formData
		})
		return data.data
	}

	const deletePost = async (formData: DeletePostRequest) => {
		const data = await $fetch<DeletePostResponse>(DeletePost.url(formData.id), {
			...useFetchConfig(),
			method: DeletePost.method,
			body: formData
		})
		return data.data
	}

	const addPostImage = async (formData: AddPostImageRequest) => {
		const data = await $fetch<AddPostImageResponse>(AddPostImage.url(), {
			...useFetchConfig(),
			method: AddPostImage.method,
			body: formData
		})
		return data.data
	}

	const removePostImage = async (formData: RemovePostImageRequest) => {
		const data = await $fetch<RemovePostImageResponse>(RemovePostImage.url(), {
			...useFetchConfig(),
			method: RemovePostImage.method,
			body: formData
		})
		return data.data
	}

	const swapPostImage = async (formData: SwapPostImageRequest) => {
		const data = await $fetch<SwapPostImageResponse>(SwapPostImage.url(), {
			...useFetchConfig(),
			method: SwapPostImage.method,
			body: formData
		})
		return data.data
	}

	const addPostVideo = async (formData: AddPostVideoRequest) => {
		const data = await $fetch<AddPostVideoResponse>(AddPostVideo.url(), {
			...useFetchConfig(),
			method: AddPostVideo.method,
			body: formData
		})
		return data.data
	}

	const removePostVideo = async (formData: RemovePostVideoRequest) => {
		const data = await $fetch<RemovePostVideoResponse>(RemovePostVideo.url(), {
			...useFetchConfig(),
			method: RemovePostVideo.method,
			body: formData
		})
		return data.data
	}

	const swapPostVideo = async (formData: SwapPostVideoRequest) => {
		const data = await $fetch<SwapPostVideoResponse>(SwapPostVideo.url(), {
			...useFetchConfig(),
			method: SwapPostVideo.method,
			body: formData
		})
		return data.data
	}

	const updatePostNum = async (formData: UpdatePostNumRequest) => {
		const data = await $fetch<UpdatePostNumResponse>(UpdatePostNum.url(formData.id), {
			...useFetchConfig(),
			method: UpdatePostNum.method,
			body: formData
		})
		return data.data
	}

	const batchUpdatePostNums = async (formData: BatchUpdatePostNumsRequest) => {
		const data = await $fetch<ApiResponse>(BatchUpdatePostNums.url(), {
			...useFetchConfig(),
			method: BatchUpdatePostNums.method,
			body: { ...formData, items: JSON.stringify(formData.items) }
		})
		return data.data
	}

	return {
		...state,
		getPost,
		getPostBySlug,
		searchPosts,
		createPost,
		editPost,
		deletePost,
		addPostImage,
		removePostImage,
		swapPostImage,
		addPostVideo,
		removePostVideo,
		swapPostVideo,
		getTempPost,
		updatePostNum,
		batchUpdatePostNums
	}
})

// post :: get temp post
export interface GetTempPostRequest {
	company_id: number | null
	type: PostType
}
export interface GetTempPostResponse extends ApiResponse {
	data: Post
}
const GetTempPost = {
	method: "POST" as const,
	url: () => "posts/temp"
}

// post :: create post
export interface CreatePostRequest {
	company_id: number | null
	title_en?: string
	title_tr?: string
	title_fa?: string
	content_en?: string
	content_tr?: string
	content_fa?: string
	description_en?: string
	description_tr?: string
	description_fa?: string
	info_en?: string
	info_tr?: string
	info_fa?: string
	features_en?: PostFeature[]
	features_tr?: PostFeature[]
	features_fa?: PostFeature[]
	options?: number[]
	price?: number
	discounted_price?: number
	type?: string
	status?: string
	slug?: string
	category_ids?: string | null
	image_id?: number | null
	published?: string
	custom_data?: Record<string, any> | null
	custom_fields?: CustomPostTypeField[] | null
	custom_field_values?: Record<string, string> | null
}
export interface CreatePostResponse extends ApiResponse {
	data: Post
}
const CreatePost = {
	method: "POST" as const,
	url: () => "posts/create"
}

// post :: edit post
export interface EditPostRequest {
	company_id: number | null
	id: number
	title_en?: string
	title_tr?: string
	title_fa?: string
	subtitle_en?: string
	subtitle_tr?: string
	subtitle_fa?: string
	content_en?: string
	content_tr?: string
	content_fa?: string
	short_content_en?: string
	short_content_tr?: string
	short_content_fa?: string
	description_en?: string
	description_tr?: string
	description_fa?: string
	info_en?: PostInfo[]
	info_tr?: PostInfo[]
	info_fa?: PostInfo[]
	features_en?: PostFeature[]
	features_tr?: PostFeature[]
	features_fa?: PostFeature[]
	options?: number[]
	price?: number
	discounted_price?: number
	type?: string
	status?: string
	category_ids?: string | null
	keywords?: string[]
	slug?: string
	published?: string | null
	color?: string | null
	amount_unit?: string | null
	amount_value?: number | null
	kind?: string | null
	step_ids?: number[]
	image_id?: number | null
	cover_id?: number | null
	video_id?: number | null
	custom_data?: Record<string, any> | null
	custom_fields?: CustomPostTypeField[] | null
	custom_field_values?: Record<string, string> | null
	location_country?: string
	location_province?: string
	location_city?: string
	location_address?: string
	location_postal_code?: string
	location_latitude?: number
	location_longitude?: number
}
export interface EditPostResponse extends ApiResponse {
	data: Post
}
const EditPost = {
	method: "POST" as const,
	url: () => "posts/edit"
}

// post :: delete post
export interface DeletePostRequest {
	id: number
	company_id: number | null
}
export interface DeletePostResponse extends ApiResponse {
	data: null
}
const DeletePost = {
	method: "POST" as const,
	url: (id: any) => `posts/delete/${id}`
}

// post :: get post
export interface GetPostRequest {
	id: number
	company_id: number | null
	type: PostType
}
export interface GetPostResponse extends ApiResponse {
	data: {
		post: Post
	}
}
const GetPost = {
	method: "GET" as const,
	url: (id: any) => `posts/${id}`
}

// post :: get post by slug
export interface GetPostBySlugRequest {
	slug: string
	company_id: number | null
	type: PostType
}
export interface GetPostBySlugResponse extends ApiResponse {
	data: {
		post: Post
	}
}
const GetPostBySlug = {
	method: "GET" as const,
	url: (slug: any) => `posts/slug/${slug}`
}

// post :: search posts
export interface SearchPostsRequest {
	search?: string
	page?: number
	limit?: number
	status?: PostStatus
	company_id?: number | null
	type?: PostType
	kind?: ProductKind
	category_ids?: string | null
	category_slugs?: string | null
	parent_category_ids?: string | null
	parent_category_slugs?: string | null
	option_groups?: string | null
	from_price?: number
	to_price?: number
	order_by?: string
	direction?: string
	sort_by_num?: boolean
	keyword_slugs?: string
}
export interface SearchPostsResponse extends ApiResponse {
	data: {
		posts: Post[]
		total: number
	}
}
const SearchPosts = {
	method: "GET" as const,
	url: () => `posts/search`
}

// post :: add post image
export interface AddPostImageRequest {
	id: number
	image_id: number
	type: PostImageType
	custom_key?: string
}
export interface AddPostImageResponse extends ApiResponse {
	data: Post
}
const AddPostImage = {
	method: "POST" as const,
	url: () => `posts/add-image`
}

// post :: remove post image
export interface RemovePostImageRequest {
	id: number
	image_id?: number
	type: PostImageType
	custom_key?: string
}
export interface RemovePostImageResponse extends ApiResponse {
	data: Post
}
const RemovePostImage = {
	method: "POST" as const,
	url: () => `posts/remove-image`
}

// post :: swap post image
export interface SwapPostImageRequest {
	id: number
	image_1_id: number
	image_2_id: number
	type: PostImageType
}
export interface SwapPostImageResponse extends ApiResponse {
	data: Post
}
const SwapPostImage = {
	method: "POST" as const,
	url: () => `posts/swap-image`
}

// post :: add post video
export interface AddPostVideoRequest {
	id: number
	video_id: number
}
export interface AddPostVideoResponse extends ApiResponse {
	data: Post
}
const AddPostVideo = {
	method: "POST" as const,
	url: () => `posts/add-video`
}

// post :: remove post video
export interface RemovePostVideoRequest {
	id: number
	video_id: number
}
export interface RemovePostVideoResponse extends ApiResponse {
	data: Post
}
const RemovePostVideo = {
	method: "POST" as const,
	url: () => `posts/remove-video`
}

// post :: swap post video
export interface SwapPostVideoRequest {
	id: number
	video_1_id: number
	video_2_id: number
}
export interface SwapPostVideoResponse extends ApiResponse {
	data: Post
}
const SwapPostVideo = {
	method: "POST" as const,
	url: () => `posts/swap-video`
}

// post :: update num
export interface UpdatePostNumRequest {
	id: number
	company_id: number | null
	num: number | null
}
export interface UpdatePostNumResponse extends ApiResponse {
	data: Post
}
const UpdatePostNum = {
	method: "POST" as const,
	url: (id: number) => `posts/update-num/${id}`
}

// post :: batch update nums
export interface BatchUpdatePostNumsRequest {
	company_id: number | null
	items: { id: number; num: number | null }[]
}
const BatchUpdatePostNums = {
	method: "POST" as const,
	url: () => `posts/batch-update-nums`
}

export const usePostApiUrl = () => ({
	CreatePost,
	EditPost,
	DeletePost,
	GetPost,
	GetPostBySlug,
	SearchPosts,
	AddPostImage,
	RemovePostImage,
	SwapPostImage,
	AddPostVideo,
	RemovePostVideo,
	SwapPostVideo,
	UpdatePostNum,
	BatchUpdatePostNums
})
